/**
 * TypeScript types for Subscription API responses.
 *
 * Subscriptions manage user access to premium features like
 * TasteInsights generation, unlimited lists, and advanced voting modes.
 */

/**
 * Subscription tier enum matching backend SubscriptionTier.
 */
export enum SubscriptionTier {
  FREE = 0,
  PLUS = 1,
  PRO = 3,
}

/**
 * Subscription status.
 */
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'incomplete' | 'trialing';

/**
 * Subscription plan interval.
 */
export type PlanInterval = 'month' | 'year';

/**
 * A member's subscription data from the API (`SubscriptionSerializer`). The API
 * also serves `tierName`, `isPremium`, `isActive`, `createdAt` and `updatedAt`;
 * they are not declared here because nothing in the app reads them (standing is
 * derived from `tier` + `status`, see `useIsPremium`). It does NOT serve the
 * Stripe customer or subscription ids (Stripe identifiers stay server-side).
 */
export interface Subscription {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  entitlements: Entitlements;
}

/**
 * `archive_access`/`issue_cadence` (`services/subscription_service.py:187,191`
 * in `paire-api-d51d`, D51f Phase 1) — `'full'` (premium) grants access to
 * every past ready issue; `'current_only'` (free) limits archive reads to
 * the member's own current issue. `'daily'` (premium) vs `'weekly'` (free)
 * cadence.
 */
export type ArchiveAccess = 'full' | 'current_only';
export type IssueCadence = 'daily' | 'weekly';

/**
 * Feature entitlements based on subscription tier.
 */
export interface Entitlements {
  /** Can use TasteMatching feature (all users) */
  tasteMatching: boolean;
  /** Can use TasteMatching (all users) */
  personalTasteMatching: boolean;
  /** Max personal lists (null = unlimited) */
  personalListLimit: number | null;
  /** Max group/collaborative lists (null = unlimited) */
  groupListLimit: number | null;
  /** Max members per group list */
  groupMemberLimit: number;
  /** Max restaurants per group list */
  groupRestaurantLimit: number;
  /** Can use Simple voting mode (all users) */
  simpleVoting: boolean;
  /** Can use Survivor voting mode (Paire+ only) */
  survivorVoting: boolean;
  /** Can use Ranking voting mode (Paire+ only) */
  rankingVoting: boolean;
  /** Can generate TasteInsights (Paire+ only) */
  tasteInsightsGeneration: boolean;
  /** Can view others' TasteInsights (all users) */
  tasteInsightsViewing: boolean;
  /** D51f — Digest archive access window. */
  archiveAccess: ArchiveAccess;
  /** D51f — Digest generation cadence. */
  issueCadence: IssueCadence;
}

/**
 * Subscription plan available for purchase.
 */
export interface SubscriptionPlan {
  id: number;
  tier: SubscriptionTier;
  interval: PlanInterval;
  priceUsd: string;
  stripePriceId: string;
  isActive: boolean;
}

/**
 * Request to create a Stripe Checkout session.
 */
export interface CreateCheckoutSessionRequest {
  priceId: string;
  idempotencyToken: string;
  successUrl?: string;
  cancelUrl?: string;
  coupon?: string;
}

/**
 * Response from creating a Stripe Checkout session.
 */
export interface CheckoutSessionResponse {
  checkoutUrl: string;
  sessionId: string;
}

/**
 * Response from creating a Stripe Customer Portal session.
 */
export interface PortalSessionResponse {
  portalUrl: string;
}

/**
 * Feature check response.
 */
export interface FeatureCheckResponse {
  hasAccess: boolean;
  requiredTier: SubscriptionTier;
  requiredTierDisplay: string;
  limitReached?: boolean;
  currentCount?: number;
  limit?: number;
}

/**
 * Feature names that can be checked for access.
 */
export type FeatureName =
  | 'taste_matching'
  | 'personal_taste_matching'
  | 'personal_lists'
  | 'group_lists'
  | 'simple_voting'
  | 'survivor_voting'
  | 'ranking_voting'
  | 'taste_insights_generation'
  | 'taste_insights_viewing';

/**
 * Context for upgrade prompts.
 */
export type UpgradePromptContext =
  | 'tasteInsights'
  | 'unlimitedLists'
  | 'advancedVoting'
  | 'groupCapacity'
  | 'issueArchive'
  | 'dailyCadence';

/**
 * Copy for upgrade prompts by context.
 */
export interface UpgradePromptCopy {
  title: string;
  description: string;
}

/**
 * Upgrade prompt copy by context.
 */
export const UPGRADE_PROMPT_COPY: Record<UpgradePromptContext, UpgradePromptCopy> = {
  tasteInsights: {
    title: 'Unlock Your TasteInsights',
    description: 'Get an AI-generated taste profile that shows your dining personality',
  },
  unlimitedLists: {
    title: 'Go Unlimited',
    description: 'Create as many lists as you need to organize your dining adventures',
  },
  advancedVoting: {
    title: 'Advanced Voting Modes',
    description: 'Use Survivor and Ranking modes to make group decisions more fun',
  },
  groupCapacity: {
    title: 'Expand Your Group',
    description: 'Add up to 8 members and 25 restaurants to your collaborative lists',
  },
  issueArchive: {
    title: 'Unlock Every Past Issue',
    description: 'Every past issue, saved and ready whenever you want it.',
  },
  // FINAL wording (D51f Phase 5, reviewed together with the masthead link and
  // the closing card in `copy/digest.ts`). One promise everywhere — "every
  // morning", the literal FR-P13 wording — so the title and description no
  // longer disagree ("morning" vs "daily"). NFR-1 sets no concrete morning
  // cutoff; the promise leans on the nightly batch finishing before members
  // wake (a fixed-UTC run, OQ-4), which is a product call, not a UI one. Sells
  // `issueCadence`, not `archiveAccess` (that is `issueArchive` above).
  dailyCadence: {
    title: 'Get an Issue Every Morning',
    description: 'A fresh issue every morning, not just once a week.',
  },
};

/**
 * Helper to get tier display name.
 */
export function getTierDisplayName(tier: SubscriptionTier): string {
  switch (tier) {
    case SubscriptionTier.FREE:
      return 'Free';
    case SubscriptionTier.PLUS:
      return 'Paire+';
    case SubscriptionTier.PRO:
      return 'Pro';
    default:
      return 'Unknown';
  }
}

/**
 * Helper to get a tier-aware upgrade CTA (e.g. "Upgrade to Paire+" vs "Upgrade to Pro").
 */
export function getUpgradePromptCta(tier: SubscriptionTier): string {
  return `Upgrade to ${getTierDisplayName(tier)}`;
}

/**
 * Helper to check if tier is premium.
 */
export function isPremiumTier(tier: SubscriptionTier): boolean {
  return tier >= SubscriptionTier.PLUS;
}
